import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FaStar, FaWhatsapp } from "react-icons/fa";
import { formatDistanceKm } from "../../utils/calculateDistance.js";
import {
  getBanquetMinimumGuestRange,
  getBanquetNonVegPrice,
  getBanquetVegPrice,
  getParkingCapacity,
  hasNonVegFood,
  hasValetParking,
  isAlcoholAllowed,
  isParkingAvailable,
} from "../../utils/banquetFilters.js";
import { buildBanquetWhatsappLink } from "../../utils/banquetWhatsapp.js";
import { getPrimaryRenderableBanquetImage } from "../../utils/banquetImageUtils.js";

const currency = new Intl.NumberFormat("en-IN");

const buildSecondaryChips = (banquet) => {
  const alcoholAllowed = isAlcoholAllowed(banquet);
  const parkingCapacity = getParkingCapacity(banquet);
  const parkingAvailable = isParkingAvailable(banquet);
  const chips = [
    parkingAvailable ? "Parking Available" : null,
    parkingCapacity ? `${parkingCapacity} Parking` : null,
    hasValetParking(banquet) ? "Valet Parking" : null,
    alcoholAllowed === true ? "Alcohol Allowed" : alcoholAllowed === false ? "No Alcohol" : null,
    banquet.acHall ? "AC Hall" : null,
    banquet.decorationServices?.decorPackagesAvailable || banquet.decorPolicy ? "Decor Available" : null,
    banquet.eventTypes?.some((eventType) => String(eventType).toLowerCase().includes("birthday")) ? "Birthday Friendly" : null,
    banquet.indoorOutdoor.includes("lawn") ? "Lawn" : null,
    banquet.roomsAvailable ? `${banquet.roomCount} Rooms` : null,
  ];

  return chips.filter(Boolean);
};

export default function BanquetCard({ banquet, searchContext, showLiveDistanceBadge = false }) {
  const navigate = useNavigate();
  const distanceLabel = typeof banquet.distanceKm === "number" ? `Approx. ${formatDistanceKm(banquet.distanceKm)}` : "";
  const primaryImage = getPrimaryRenderableBanquetImage(banquet.images);
  const rating = Number.isFinite(banquet.googleRating) ? banquet.googleRating : 4.0;
  const ratingCount = Number.isFinite(banquet.googleRatingsTotal) ? banquet.googleRatingsTotal : 0;
  const minGuests = Number.isFinite(banquet.guestMin) ? banquet.guestMin : 50;
  const maxGuests = Number.isFinite(banquet.guestMax) ? banquet.guestMax : 250;
  const minimumGuestRange = getBanquetMinimumGuestRange(banquet) || `${minGuests}-${maxGuests}`;
  const seatingCapacity = banquet.capacityDetails?.seatingCapacity || minGuests;
  const floatingCapacity = banquet.capacityDetails?.floatingCapacity || maxGuests;
  const vegPrice = getBanquetVegPrice(banquet);
  const nonVegPrice = getBanquetNonVegPrice(banquet);
  const nonVegAvailable = hasNonVegFood(banquet);
  const startingPrice = Math.min(vegPrice || Number.POSITIVE_INFINITY, nonVegPrice || Number.POSITIVE_INFINITY);
  const [cardImage, setCardImage] = useState(primaryImage);
  const detailQuery = new URLSearchParams();

  useEffect(() => {
    setCardImage(primaryImage);
  }, [primaryImage]);

  if (searchContext?.pincode) {
    detailQuery.set("pincode", searchContext.pincode);
    detailQuery.set("radius", searchContext.radius);
  }

  if (searchContext?.banquetTestMode) {
    detailQuery.set("banquetTestMode", "1");
  }

  const detailUrl = detailQuery.toString() ? `/banquets/${banquet.slug}?${detailQuery.toString()}` : `/banquets/${banquet.slug}`;
  const pageUrl = typeof window !== "undefined" ? `${window.location.origin}${detailUrl}` : detailUrl;
  const whatsappLink = buildBanquetWhatsappLink({
    banquet,
    pageUrl,
  });
  const stopCardNavigation = (event) => {
    event.stopPropagation();
  };
  const openDetails = () => {
    navigate(detailUrl, { state: { searchContext } });
  };
  const handleDetailLinkClick = (event) => {
    event.stopPropagation();
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) {
      return;
    }
    event.preventDefault();
    openDetails();
  };
  const handleCardClick = (event) => {
    if (event.defaultPrevented || event.target.closest("a, button, input, select, textarea")) {
      return;
    }
    openDetails();
  };
  const handleCardKeyDown = (event) => {
    if (event.key !== "Enter" && event.key !== " ") {
      return;
    }
    if (event.target.closest("a, button, input, select, textarea")) {
      return;
    }
    event.preventDefault();
    openDetails();
  };

  return (
    <article
      role="link"
      tabIndex={0}
      onClick={handleCardClick}
      onKeyDown={handleCardKeyDown}
      aria-label={`View details for ${banquet.name}`}
      className="min-w-0 cursor-pointer overflow-hidden rounded-[24px] border border-amber-100/80 bg-gradient-to-br from-white via-white to-emerald-50/35 shadow-[0_22px_55px_-34px_rgba(15,23,42,0.5)] ring-1 ring-white/80 transition duration-300 hover:-translate-y-0.5 hover:border-amber-200 hover:shadow-[0_30px_70px_-38px_rgba(15,23,42,0.55)] focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 sm:rounded-[28px]"
    >
      <div className="grid min-w-0 md:grid-cols-[360px_minmax(0,1fr)] lg:grid-cols-[400px_minmax(0,1fr)]">
        <Link
          to={detailUrl}
          state={{ searchContext }}
          onClick={handleDetailLinkClick}
          aria-label={`View details for ${banquet.name}`}
          className="relative m-3 block h-56 cursor-pointer overflow-hidden rounded-[22px] bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 focus-visible:ring-inset sm:h-64 md:h-[calc(100%-1.5rem)]"
        >
          <img
            src={cardImage}
            alt={banquet.name}
            className="h-full w-full object-cover transition duration-500 hover:scale-[1.03]"
            loading="lazy"
            onError={() => setCardImage("/images/banquet-theme-balloon-decoration-normal--decorzoom.webp")}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950/50 via-slate-950/10 to-amber-100/10" />
          <div className="absolute left-3 right-3 top-3 max-w-[calc(100%-1.5rem)] rounded-full border border-white/55 bg-white/95 px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.12em] text-emerald-950 shadow-[0_10px_24px_-16px_rgba(15,23,42,0.8)] backdrop-blur">
            {banquet.venueType}
          </div>
          <div className="absolute bottom-3 left-3 right-3 inline-flex max-w-[calc(100%-1.5rem)] items-center gap-1 rounded-full border border-amber-200/70 bg-white/95 px-3 py-1.5 text-xs font-bold text-slate-800 shadow-[0_12px_28px_-18px_rgba(15,23,42,0.8)] backdrop-blur sm:right-auto">
            <FaStar className="text-[11px] text-amber-500" />
            <span className="truncate">{rating} <span className="text-slate-400">|</span> {currency.format(ratingCount)} reviews</span>
          </div>
        </Link>

        <div className="flex min-w-0 flex-col gap-2.5 p-3 pt-1 md:gap-4 md:p-5 md:pl-3 md:pt-5">
          <Link
            to={detailUrl}
            state={{ searchContext }}
            onClick={handleDetailLinkClick}
            className="block min-w-0 cursor-pointer rounded-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 focus-visible:ring-offset-2"
          >
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div className="min-w-0">
              <h2 className="break-words text-base font-semibold leading-6 text-slate-900 md:text-xl md:leading-7">{banquet.name}</h2>
              <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-slate-600">
                <span>{banquet.locality || banquet.area || banquet.city || "Location unavailable"}</span>
                {typeof banquet.distanceKm === "number" && showLiveDistanceBadge ? (
                  <span className="motion-safe:animate-near-me-badge-pulse inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-bold text-emerald-800 shadow-sm">
                    <span aria-hidden="true">📍</span>
                    {formatDistanceKm(banquet.distanceKm)}
                  </span>
                ) : distanceLabel ? (
                  <>
                    <span className="text-slate-300">&middot;</span>
                    <span className="font-medium text-sky-700">{distanceLabel}</span>
                  </>
                ) : null}
              </div>
              <p className="mt-1.5 line-clamp-2 text-sm leading-5 text-slate-600 md:mt-2 md:leading-6">{banquet.shortDescription}</p>
              <div className="mt-2 inline-flex max-w-full rounded-2xl border border-emerald-200 bg-emerald-50 px-2.5 py-1.5 text-xs font-bold text-emerald-800 md:mt-3 md:px-3 md:py-2 md:text-sm">
                Perfect for {minimumGuestRange.replace("-", "-")} guests
              </div>
            </div>

            <div className="min-w-0 rounded-2xl border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-sm text-amber-900 lg:w-auto lg:shrink-0 md:px-3 md:py-2">
              <div className="flex items-center gap-1 font-semibold">
                <FaStar className="text-[12px]" />
                <span>{rating}</span>
              </div>
              <p className="mt-0.5 text-xs text-amber-800">{currency.format(ratingCount)} reviews</p>
            </div>
          </div>
          </Link>

          {/* Mobile-only compact summary (<640px) — replaces the boxed layout below with a single dense row per group. */}
          <Link
            to={detailUrl}
            state={{ searchContext }}
            onClick={handleDetailLinkClick}
            className="flex min-w-0 cursor-pointer flex-wrap items-center gap-x-2.5 gap-y-1 rounded-2xl border border-emerald-100/80 bg-gradient-to-br from-emerald-50/80 via-white to-amber-50/70 px-3 py-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 focus-visible:ring-offset-2 sm:hidden"
          >
            <span className="inline-flex items-baseline gap-1 text-base font-black text-slate-950">
              {Number.isFinite(startingPrice) ? `₹${currency.format(startingPrice)}` : "On request"}
              <span className="text-[10px] font-medium text-slate-500">starting</span>
            </span>
            <span aria-hidden="true" className="text-slate-300">&middot;</span>
            <span className="inline-flex items-baseline gap-1 text-base font-black text-emerald-700">
              {minimumGuestRange}
              <span className="text-[10px] font-medium text-slate-500">min booking</span>
            </span>
            <span aria-hidden="true" className="text-slate-300">&middot;</span>
            <span className="inline-flex items-baseline gap-1 text-base font-black text-slate-950">
              {currency.format(seatingCapacity)}/{currency.format(floatingCapacity)}
              <span className="text-[10px] font-medium text-slate-500">seated/floating</span>
            </span>
          </Link>

          <Link
            to={detailUrl}
            state={{ searchContext }}
            onClick={handleDetailLinkClick}
            className="flex min-w-0 cursor-pointer flex-wrap items-center gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 focus-visible:ring-offset-2 sm:hidden"
          >
            <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-50 px-2.5 py-1 text-xs font-semibold text-slate-800 ring-1 ring-slate-200/70">
              <span aria-hidden="true" className="h-2 w-2 shrink-0 rounded-full bg-emerald-600" />
              Veg {vegPrice ? `₹${currency.format(vegPrice)}` : "N/A"}
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-50 px-2.5 py-1 text-xs font-semibold text-slate-800 ring-1 ring-slate-200/70">
              <span aria-hidden="true" className="h-2 w-2 shrink-0 rounded-full bg-orange-600" />
              Non-Veg {nonVegPrice ? `₹${currency.format(nonVegPrice)}` : nonVegAvailable ? "Available" : "N/A"}
            </span>
            {!nonVegAvailable ? <span className="inline-flex rounded-full bg-emerald-100 px-2 py-1 text-[10px] font-semibold text-emerald-800">Veg Only</span> : null}
          </Link>

          {/* Tablet/desktop (>=640px) — unchanged boxed layout. */}
          <Link
            to={detailUrl}
            state={{ searchContext }}
            onClick={handleDetailLinkClick}
            className="hidden min-w-0 cursor-pointer gap-2 rounded-[22px] border border-emerald-100/80 bg-gradient-to-br from-emerald-50/80 via-white to-amber-50/70 p-2 shadow-inner focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 focus-visible:ring-offset-2 sm:grid sm:grid-cols-3 md:gap-3 md:p-3"
          >
            <div className="min-w-0 rounded-2xl bg-white px-2.5 py-2 ring-1 ring-slate-200/70 md:px-3 md:py-3">
              <p className="break-words text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500 sm:tracking-[0.18em]">Starting Price</p>
              <p className="mt-0.5 text-sm font-semibold text-slate-900 md:mt-1 md:text-base">
                {Number.isFinite(startingPrice) ? `₹${currency.format(startingPrice)}` : "On request"}
              </p>
            </div>
            <div className="min-w-0 rounded-2xl bg-emerald-600 px-2.5 py-2 text-white ring-1 ring-emerald-600 md:px-3 md:py-3">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-emerald-50">Minimum Booking</p>
              <p className="mt-0.5 break-words text-sm font-bold md:mt-1 md:text-base">
                {minimumGuestRange} <span className="text-xs font-medium text-emerald-50 md:text-sm">guests</span>
              </p>
            </div>
            <div className="min-w-0 rounded-2xl bg-white px-2.5 py-2 ring-1 ring-slate-200/70 md:px-3 md:py-3">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Capacity</p>
              <p className="mt-0.5 break-words text-sm font-semibold text-slate-900 md:mt-1 md:text-base">
                {currency.format(seatingCapacity)} seated
              </p>
              <p className="text-xs font-medium text-slate-500">{currency.format(floatingCapacity)} floating</p>
            </div>
          </Link>

          <Link
            to={detailUrl}
            state={{ searchContext }}
            onClick={handleDetailLinkClick}
            className="hidden min-w-0 cursor-pointer gap-2 rounded-[20px] bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 focus-visible:ring-offset-2 sm:grid sm:grid-cols-2 md:gap-3"
          >
            <div className="min-w-0 rounded-2xl bg-slate-50 px-2.5 py-2 ring-1 ring-slate-200/70 md:px-3 md:py-3">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Veg</p>
              <p className="mt-0.5 break-words text-sm font-semibold text-slate-900 md:mt-1 md:text-base">
                {vegPrice ? `₹${currency.format(vegPrice)}` : "N/A"} {vegPrice ? <span className="text-sm font-medium text-slate-500">/ plate</span> : null}
              </p>
              {!nonVegAvailable ? <span className="mt-1.5 inline-flex rounded-full bg-emerald-100 px-2 py-1 text-[11px] font-semibold text-emerald-800 md:mt-2">Veg Only</span> : null}
            </div>
            <div className="min-w-0 rounded-2xl bg-slate-50 px-2.5 py-2 ring-1 ring-slate-200/70 md:px-3 md:py-3">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Non-Veg</p>
              <p className="mt-0.5 break-words text-sm font-semibold text-slate-900 md:mt-1 md:text-base">
                {nonVegPrice ? `₹${currency.format(nonVegPrice)}` : "N/A"}{" "}
                {nonVegPrice ? <span className="text-sm font-medium text-slate-500">/ plate</span> : null}
              </p>
              {nonVegAvailable ? <span className="mt-1.5 inline-flex rounded-full bg-orange-100 px-2 py-1 text-[11px] font-semibold text-orange-800 md:mt-2">Non-Veg Available</span> : null}
            </div>
          </Link>

          <Link
            to={detailUrl}
            state={{ searchContext }}
            onClick={handleDetailLinkClick}
            className="flex min-w-0 cursor-pointer flex-wrap gap-2 rounded-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 focus-visible:ring-offset-2"
          >
            {buildSecondaryChips(banquet).map((tag) => (
              <span key={tag} className="min-h-[30px] max-w-full rounded-2xl border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-xs font-medium leading-4 text-slate-700 md:min-h-[36px] md:rounded-full md:px-3 md:py-2">
                {tag}
              </span>
            ))}
            <span className="min-h-[30px] max-w-full rounded-2xl border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-xs font-medium leading-4 text-slate-700 md:min-h-[36px] md:rounded-full md:px-3 md:py-2">
              Up to {currency.format(maxGuests)} guests
            </span>
          </Link>

          <div className="mt-auto flex flex-col gap-2 border-t border-slate-200 pt-3 sm:flex-row sm:items-center sm:justify-between md:gap-3 md:pt-4">
            <a
              href={banquet.mapUrl}
              target="_blank"
              rel="noreferrer"
              onClick={stopCardNavigation}
              className="inline-flex min-h-[44px] items-center text-sm font-semibold text-slate-600 transition hover:text-emerald-700"
            >
              View on Map
            </a>

            <div className="grid w-full grid-cols-2 gap-2 sm:w-auto">
              <Link
                to={detailUrl}
                state={{ searchContext }}
                onClick={handleDetailLinkClick}
                className="inline-flex min-h-[40px] items-center justify-center rounded-full border border-emerald-200 bg-white px-3 py-2 text-sm font-bold text-emerald-800 shadow-sm transition hover:border-emerald-400 hover:bg-emerald-50 md:min-h-[44px] md:px-4 md:py-2.5"
              >
                View Details
              </Link>
              <a
                href={whatsappLink}
                target="_blank"
                rel="noreferrer"
                onClick={stopCardNavigation}
                className="inline-flex min-h-[40px] items-center justify-center gap-2 rounded-full bg-[#25D366] px-3 py-2 text-sm font-semibold text-white transition hover:brightness-95 md:min-h-[44px] md:px-4 md:py-2.5"
              >
                <FaWhatsapp className="text-base" />
              Get Best Price
              </a>
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}
